import { getRequestFailureMetadata } from 'app-components/utils/core/axiosErrorGrouping';

const maxNetworkLogsLength = 128;

type NetworkLog = {
  key: XMLHttpRequest;
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestData?: unknown;
  responseHeaders?: Record<string, string>;
  responseData?: unknown;
  status?: number;
  statusText?: string;
  startedAt?: string;
  durationMs?: number;
};

const networkLogs: NetworkLog[] = [];

// Trimming the log shifts indices, so look a request's entry up again whenever it is updated.
const findLog = (xhr: XMLHttpRequest): NetworkLog | undefined => networkLogs.find((item) => item.key === xhr);

// monkey patch XMLHttpRequest to get network logs
export function captureNetworkLogs(xhr: Pick<XMLHttpRequest, 'open' | 'setRequestHeader' | 'send'>): void {
  // Capture request before any network activity occurs:
  const { open } = xhr;
  xhr.open = function _open(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    networkLogs.push({
      key: this,
      method,
      url: String(url),
      requestHeaders: {},
    });
    if (networkLogs.length > maxNetworkLogsLength) {
      networkLogs.splice(0, networkLogs.length - maxNetworkLogsLength);
    }
    return (open as (...args: unknown[]) => void).call(this, method, url, ...rest);
  };

  const { setRequestHeader } = xhr;
  xhr.setRequestHeader = function _setRequestHeader(this: XMLHttpRequest, header: string, value: string) {
    const log = findLog(this);
    if (log) {
      log.requestHeaders[header] = value;
    }
    return setRequestHeader?.call(this, header, value);
  };

  const { send } = xhr;
  xhr.send = function _send(this: XMLHttpRequest, requestData?: Document | XMLHttpRequestBodyInit | null) {
    const log = findLog(this);
    if (log) {
      log.requestData = requestData;
      const sentAt = Date.now();
      log.startedAt = new Date(sentAt).toISOString();

      const { onloadend } = this;
      this.onloadend = function _onloadend(this: XMLHttpRequest, ...params) {
        const current = findLog(this);
        if (current) {
          current.responseHeaders = Object.fromEntries(
            this.getAllResponseHeaders()
              .split(/\r?\n/)
              .map((entry) => entry.split(': ')),
          );
          current.responseData = this.response;
          current.status = this.status;
          current.statusText = this.statusText;
          current.durationMs = Date.now() - sentAt;
        }
        return onloadend?.apply(this, params);
      };
    }

    return send.call(this, requestData);
  };
}

const parseJson = (data: unknown): unknown => {
  try {
    return JSON.parse(data as string);
  } catch (_e) {
    return data;
  }
};

export const getNetworkLogs = (): Array<Omit<NetworkLog, 'key'>> =>
  networkLogs.map((logItem) => {
    const { key, requestData, responseData, ...rest } = logItem;

    return {
      ...rest,
      requestData: parseJson(requestData),
      responseData: parseJson(responseData),
    };
  });

/** What each request was and how it ended: no bodies, query values or headers. */
export const getNetworkLogSummaries = (): Array<Record<string, unknown>> =>
  getNetworkLogs().map(
    ({ method, url, requestHeaders, responseHeaders, responseData, status, startedAt, durationMs }) => {
      const { message: _message, ...summary } = getRequestFailureMetadata({
        config: { headers: requestHeaders, method, url },
        response: { data: responseData, headers: responseHeaders, status },
      });
      return { ...summary, startedAt, durationMs };
    },
  );
